/**
 * @fileoverview Auth-related constants. Aligns with Cognito group names; session flags and
 * operator-safe logging helpers so pages and route loaders stay DRY without pages importing `routes/loaders`.
 * @module constants/auth
 */

/** Application role keys. Use these instead of string literals. */
export const AUTH_ROLES = Object.freeze({
  SUPERADMIN: "superadmin",
  CAMPAIGN: "campaign",
  REALTOR: "realtor",
  HOMEOWNER: "homeowner",
  INVESTOR: "investor",
  USER: "user",
  CONTRACTOR: "contractor",
  WHOLESALER: "wholesaler",
});

/**
 * Post-login / “home” path per {@link AUTH_ROLES} value (keys are group strings).
 * Keep in sync with the first matching path in `routes/routesConfig` `AUTHENTICATED_ROUTES`.
 */
export const ROLE_DEFAULT_PATHS = Object.freeze({
  superadmin: "/users",
  campaign: "/campaigns",
  realtor: "/properties",
  homeowner: "/properties",
  investor: "/opportunities",
  user: "/soon",
  contractor: "/soon",
  wholesaler: "/soon",
});

/** Set before redirect so `/signin` can show a one-shot account configuration dialog (see route loaders). */
export const ACCOUNT_ROLE_ISSUE_SESSION_KEY = "horaizen_session_account_role_issue_v1";
/** One-shot payload shown on `/signin` after account registration/confirmation. */
export const SIGNUP_SIGNIN_NOTICE_SESSION_KEY = "horaizen_signup_signin_notice_v1";
/** Prefilled email carried from `/signup` to `/confirm-signup` without query params. */
export const SIGNUP_CONFIRM_EMAIL_SESSION_KEY = "horaizen_signup_confirm_email_v1";
/** Distinct-email attempt log ({ h, at }[]) stored client-side to drive the anti-abuse warning. */
export const SIGNUP_PUBLIC_RATE_LIMIT_STORAGE_KEY = "horaizen_signup_public_rate_limit_v2";

/**
 * Last up to six characters for operator logs (correlates sessions without logging full email/username).
 * @param {string} [userId]
 * @returns {string}
 */
export function userIdSuffixForOperationalLog(userId) {
  const s = String(userId ?? "").trim();
  if (!s) return "(empty)";
  return s.length <= 6 ? s : s.slice(-6);
}
